import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable, InjectionToken } from '@angular/core';
import { firstValueFrom, from, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { CLIENT_ID } from '../../constants';
import { EstensaUser, SessionInformation } from '../../models/vnext.models';
import { JwtHandler } from '../jwt-handler';
import { ServerRootService } from '../../utils/routing/serverroot.service';

export const ESTENSA_MODULE_AUTHENTICATION_SERVICE = new InjectionToken('estensa.module.authentication.service');

export interface LoginData {
    domain: string;
    userName: string;
    password: string;
}

export interface IAuthenticationService {
    currentToken: {
        jwt: string;
    };
    loggedIn: boolean;
    sessionInfo: SessionInformation;
    encryptedCredentials: string;

    getFullUser(id?: string): Promise<EstensaUser>;
    getLoggedUserId(): string;
    getLoggedUniqueName(): string;
    hasSecondaryAuthentication(): boolean;
    isOpenedFromWpf(): boolean;
    initSessionInfo(): Promise<SessionInformation>;
    isAuthenticated(): boolean;
    login(loginData: LoginData): Promise<void>;
    setUserInfo(userInfo: any): void;
    openNewTab?: (page: string) => Promise<Window>;
    getUserParingToken?: () => Promise<string>;
}

export interface ITokenResponse {
    access_token: string;
    expires_in: number;
    token_type: string;
    refresh_token: string;
}

@Injectable({
    providedIn: 'root',
})
export class AuthenticationService {
    // data members

    get isAuthenticated(): boolean {
        return this.authenticationService.isAuthenticated();
    }

    get hasSecondaryAuthentication() {
        return this.authenticationService.hasSecondaryAuthentication();
    }

    get isOpenedFromWpf() {
        return this.authenticationService.isOpenedFromWpf();
    }

    // constructor

    constructor(
        @Inject(ESTENSA_MODULE_AUTHENTICATION_SERVICE) private authenticationService: IAuthenticationService,
        private httpClient: HttpClient,
        private serverRootService: ServerRootService,
    ) {}

    // public functions
    getEncryptedCredentials() {
        return this.authenticationService.encryptedCredentials;
    }

    getUserId() {
        return this.authenticationService.getLoggedUserId();
    }

    getUserName() {
        return this.authenticationService.getLoggedUniqueName();
    }

    getSessionInfo() {
        return this.authenticationService.sessionInfo;
    }

    getUserInfo(userId?: string) {
        const id = userId ? userId : this.getUserId();

        // Promise di $q a Promise nativa
        return new Promise<EstensaUser>((resolve, reject) => {
            this.authenticationService.getFullUser(id).then(resolve).catch(reject);
        });
    }

    getAdditionalUserName(userId: string) {
        return firstValueFrom(
            this.httpClient.get<string>(
                `${this.serverRootService.getServerRoot()}/Authentication/GetAdditionalUserName?userId=${userId}`,
            ),
        );
    }

    initSessionInfo() {
        return new Promise<SessionInformation>((resolve, reject) => {
            this.authenticationService.initSessionInfo().then(resolve).catch(reject);
        });
    }

    login(loginData: LoginData) {
        return from(this.authenticationService.login(loginData));
    }

    openNewTab(page: string): Observable<Window | null> {
        if (this.authenticationService.openNewTab) return from(this.authenticationService.openNewTab(page));
        return of(null);
    }

    getUserParingToken(): Observable<string> {
        if (this.authenticationService.getUserParingToken) return from(this.authenticationService.getUserParingToken());
        return of('');
    }

    /**
     * Login via ESTENSA WEB. Nel momento in cui sarà possibile navigare al client html5 da vNext,
     *   sarà necessario aggiungere il metodo setWebLoginInfo ad ESTENSA_MODULE_USER_SERVICE.
     */
    setWebLoginInfo(token: string) {
        this.authenticationService.currentToken.jwt = token;

        const userInfo = new JwtHandler().decodeToken(token);

        this.authenticationService.loggedIn = true;
        this.authenticationService.setUserInfo(userInfo);
    }

    // C.f.r Estensa.Web.Html5\src\app\Authentication\AuthenticationService.ts
    estensaWebLogin(token: string) {
        const url = this.serverRootService.getAuthRoot() + '/login/estensaWEB';

        const body = new HttpParams()
            .set('pairingToken', token)
            .set('grant_type', 'client_credentials')
            .set('client_id', CLIENT_ID)
            .toString();

        const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');

        return firstValueFrom(
            this.httpClient.post<ITokenResponse>(url, body, { headers }).pipe(
                map((data: ITokenResponse) => {
                    localStorage.setItem('access_token', data.access_token);

                    this.setWebLoginInfo(data.access_token);
                    this.authenticationService.initSessionInfo();

                    return true;
                }),
            ),
        );
    }
}
